import { useEffect, useRef, useState } from "react";
import { useWebHaptics } from "web-haptics/react";
import { MOKA_DEBUG_SELF_PLAY_COOLDOWN_MULTIPLIER, MOKA_DEBUG_SELF_PLAY_MINIMUM_COOLDOWN_MS, MOKA_MATCH_ANALYSIS_IDLE_DELAY_MS } from "@/constants";
import { getMokaMoveAnalyses, getMokaMovePolicyPercentile, getMokaSymmetryBlackWinProbability, initializeMokaEngine, passMokaTurn, playMokaCoordinate, playMokaMove } from "@/lib/moka-go-engine";
import { doesDeviceNeedDirectHapticTarget } from "@/utils/does-device-need-direct-haptic-target";
import { getGoBoardStones } from "@/utils/get-go-board-stones";
import { getMokaAreaAnalysis } from "@/utils/get-moka-area-analysis";
import { getMokaAreaScore } from "@/utils/get-moka-area-score";
import { getMokaAutomaticallyScoredGameState } from "@/utils/get-moka-automatically-scored-game-state";
import { getMokaCoordinateLabel } from "@/utils/get-moka-coordinate-label";
import { getMokaEstimatedRank } from "@/utils/get-moka-estimated-rank";
import { getMokaFinalBlackWinProbability } from "@/utils/get-moka-final-black-win-probability";
import { getMokaMatchInitialPosition } from "@/utils/get-moka-match-initial-position";
import { getMokaMatchResultMessage } from "@/utils/get-moka-match-result-message";
import { getMokaSelfPlayResultMessage } from "@/utils/get-moka-self-play-result-message";
import { isMokaGameOver } from "@/utils/is-moka-game-over";
import { scheduleBrowserIdleCallback } from "@/utils/schedule-browser-idle-callback";
import { waitForMokaResponseDelay } from "@/utils/wait-for-moka-response-delay";
const useMokaMatch = () => {
  const {
    isSupported: isVibrationApiSupported,
    trigger: triggerHaptic
  } = useWebHaptics();
  const activeGameIdReference = useRef(0);
  const isMovePendingReference = useRef(false);
  const debugInferenceDurationMsReference = useRef(0);
  const [position, setPosition] = useState(() => getMokaMatchInitialPosition("Loading Moka…"));
  const positionHistoryReference = useRef([{
    ...position,
    gameMessage: "Your turn."
  }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);
  const [hoveredCoordinate, setHoveredCoordinate] = useState(null);
  const [invalidMoveCoordinate, setInvalidMoveCoordinate] = useState(null);
  const [isEngineReady, setIsEngineReady] = useState(false);
  const [isDebugAutoPlaying, setIsDebugAutoPlaying] = useState(true);
  const [isDebugMode, setIsDebugMode] = useState(true);
  const [doesNeedDirectHapticTarget, setDoesNeedDirectHapticTarget] = useState(false);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);
  const [isAnalysisModePinned, setIsAnalysisModePinned] = useState(false);
  const [isAnalysisShortcutActive, setIsAnalysisShortcutActive] = useState(false);
  const [isMokaThinking, setIsMokaThinking] = useState(false);
  const [blackMoveAnalyses, setBlackMoveAnalyses] = useState([]);
  const {
    blackWinProbability,
    gameState,
    isGameComplete
  } = position;
  const isAnalysisModeActive = isAnalysisModePinned || isAnalysisShortcutActive;
  const isBoardDisabled = !isEngineReady || isDebugMode || isGameComplete || isMokaThinking || errorMessage !== null;
  const isHistoryNavigationDisabled = !isEngineReady || isMokaThinking || errorMessage !== null;
  const canUndo = !isHistoryNavigationDisabled && historyIndex > 0;
  const canRedo = !isHistoryNavigationDisabled && historyIndex < positionHistoryReference.current.length - 1;
  const latestMove = gameState.moveHistory.at(-1)?.coordinate ?? null;
  const isHoveredMoveIllegal = hoveredCoordinate !== null && !isBoardDisabled && playMokaCoordinate(gameState, hoveredCoordinate) === null;
  const hoveredMoveAnalysis = blackMoveAnalyses.find(moveAnalysis => moveAnalysis.coordinate.column === hoveredCoordinate?.column && moveAnalysis.coordinate.row === hoveredCoordinate?.row);
  const averageHumanMovePolicyPercentile = position.humanMovePolicyPercentileCount === 0 ? 0 : position.humanMovePolicyPercentileSum / position.humanMovePolicyPercentileCount;
  const estimatedRank = getMokaEstimatedRank(averageHumanMovePolicyPercentile, blackWinProbability, position.humanMovePolicyPercentileCount);
  const areaAnalysis = getMokaAreaAnalysis(gameState, {
    isTerritoryConservative: !isGameComplete
  });
  const winner = isGameComplete ? areaAnalysis.score > 0 ? "black" : areaAnalysis.score < 0 ? "white" : null : null;
  useEffect(() => {
    setDoesNeedDirectHapticTarget(doesDeviceNeedDirectHapticTarget(isVibrationApiSupported));
  }, [isVibrationApiSupported]);
  useEffect(() => {
    const activateAnalysisMode = event => {
      if (event.key === "Shift") {
        setIsAnalysisShortcutActive(true);
      }
    };
    const deactivateAnalysisMode = event => {
      if (event.key === "Shift") {
        setIsAnalysisShortcutActive(false);
      }
    };
    const deactivateAnalysisModeOnBlur = () => setIsAnalysisShortcutActive(false);
    window.addEventListener("keydown", activateAnalysisMode);
    window.addEventListener("keyup", deactivateAnalysisMode);
    window.addEventListener("blur", deactivateAnalysisModeOnBlur);
    return () => {
      window.removeEventListener("keydown", activateAnalysisMode);
      window.removeEventListener("keyup", deactivateAnalysisMode);
      window.removeEventListener("blur", deactivateAnalysisModeOnBlur);
    };
  }, []);
  useEffect(() => {
    let isCancelled = false;
    void initializeMokaEngine().then(() => {
      if (isCancelled) {
        return;
      }
      setIsEngineReady(true);
      setPosition(currentPosition => ({
        ...currentPosition,
        gameMessage: "Debug ready."
      }));
    }).catch(error => {
      if (isCancelled) {
        return;
      }
      setErrorMessage(error instanceof Error ? error.message : "Unable to load Moka.");
    });
    return () => {
      isCancelled = true;
      activeGameIdReference.current += 1;
    };
  }, []);
  useEffect(() => {
    let isAnalysisCancelled = false;
    setBlackMoveAnalyses([]);
    setIsAnalysisLoading(false);
    if (!isAnalysisModeActive || isBoardDisabled || gameState.nextColor !== "black") {
      return;
    }
    setIsAnalysisLoading(true);
    const cancelAnalysis = scheduleBrowserIdleCallback(() => {
      void getMokaMoveAnalyses(gameState).then(nextBlackMoveAnalyses => {
        if (!isAnalysisCancelled) {
          setBlackMoveAnalyses(nextBlackMoveAnalyses);
          setIsAnalysisLoading(false);
        }
      }).catch(() => {
        if (!isAnalysisCancelled) {
          setBlackMoveAnalyses([]);
          setIsAnalysisLoading(false);
        }
      });
    }, MOKA_MATCH_ANALYSIS_IDLE_DELAY_MS);
    return () => {
      isAnalysisCancelled = true;
      cancelAnalysis();
    };
  }, [gameState, isAnalysisModeActive, isBoardDisabled]);
  const commitPosition = nextPosition => {
    const nextPositionHistory = [...positionHistoryReference.current.slice(0, historyIndex + 1), nextPosition];
    positionHistoryReference.current = nextPositionHistory;
    setPosition(nextPosition);
    setHistoryIndex(nextPositionHistory.length - 1);
  };
  const completeGame = (nextGameState, positionProgress) => {
    const automaticallyScoredGameState = getMokaAutomaticallyScoredGameState(nextGameState);
    const score = getMokaAreaScore(automaticallyScoredGameState);
    isMovePendingReference.current = false;
    setIsDebugAutoPlaying(false);
    setIsMokaThinking(false);
    commitPosition({
      ...positionProgress,
      blackWinProbability: getMokaFinalBlackWinProbability(score),
      gameMessage: isDebugMode ? getMokaSelfPlayResultMessage(score) : getMokaMatchResultMessage(automaticallyScoredGameState),
      gameState: automaticallyScoredGameState,
      isGameComplete: true,
      stones: getGoBoardStones(automaticallyScoredGameState.board)
    });
  };
  const playMokaResponse = async (humanMoveResult, gameId, humanMovePolicyPercentilePromise) => {
    const responseDelayPromise = waitForMokaResponseDelay();
    try {
      const humanMovePolicyPercentile = await humanMovePolicyPercentilePromise;
      if (activeGameIdReference.current !== gameId) {
        return;
      }
      const humanMovePolicyPercentileCount = humanMovePolicyPercentile === null ? position.humanMovePolicyPercentileCount : position.humanMovePolicyPercentileCount + 1;
      const humanMovePolicyPercentileSum = humanMovePolicyPercentile === null ? position.humanMovePolicyPercentileSum : position.humanMovePolicyPercentileSum + humanMovePolicyPercentile;
      if (isMokaGameOver(humanMoveResult.nextState)) {
        completeGame(humanMoveResult.nextState, {
          humanMovePolicyPercentileCount,
          humanMovePolicyPercentileSum,
          mokaResponseDurationMs: position.mokaResponseDurationMs
        });
        return;
      }
      await responseDelayPromise;
      if (activeGameIdReference.current !== gameId) {
        return;
      }
      const responseStartTime = performance.now();
      const mokaMoveResult = await playMokaMove(humanMoveResult.nextState);
      if (activeGameIdReference.current !== gameId) {
        return;
      }
      if (isMokaGameOver(mokaMoveResult.nextState)) {
        completeGame(mokaMoveResult.nextState, {
          humanMovePolicyPercentileCount,
          humanMovePolicyPercentileSum,
          mokaResponseDurationMs: performance.now() - responseStartTime
        });
        return;
      }
      const nextBlackWinProbability = mokaMoveResult.blackWinProbability ?? (await getMokaSymmetryBlackWinProbability(mokaMoveResult.nextState));
      if (activeGameIdReference.current !== gameId) {
        return;
      }
      const responseDurationMs = performance.now() - responseStartTime;
      const didMokaPlayStone = Boolean(mokaMoveResult.nextState.moveHistory.at(-1)?.coordinate);
      isMovePendingReference.current = false;
      setIsMokaThinking(false);
      commitPosition({
        blackWinProbability: nextBlackWinProbability,
        gameMessage: didMokaPlayStone ? "Your turn." : "Moka passed. Your turn.",
        gameState: mokaMoveResult.nextState,
        humanMovePolicyPercentileCount,
        humanMovePolicyPercentileSum,
        isGameComplete: false,
        mokaResponseDurationMs: responseDurationMs,
        stones: mokaMoveResult.stones
      });
    } catch (error) {
      if (activeGameIdReference.current !== gameId) {
        return;
      }
      isMovePendingReference.current = false;
      setErrorMessage(error instanceof Error ? error.message : "Moka could not play a move.");
      setIsMokaThinking(false);
    }
  };
  const playHumanMove = (humanMoveResult, attemptedCoordinate, didTriggerHapticDirectly) => {
    if (isBoardDisabled || isMovePendingReference.current) {
      return;
    }
    if (!humanMoveResult) {
      if (!didTriggerHapticDirectly) {
        void triggerHaptic("rigid");
      }
      setInvalidMoveCoordinate(attemptedCoordinate);
      setPosition(currentPosition => ({
        ...currentPosition,
        gameMessage: "That move is not legal."
      }));
      return;
    }
    if (!didTriggerHapticDirectly) {
      void triggerHaptic("selection");
    }
    const gameId = activeGameIdReference.current;
    const humanMovePolicyPercentilePromise = attemptedCoordinate ? getMokaMovePolicyPercentile(gameState, attemptedCoordinate) : Promise.resolve(null);
    isMovePendingReference.current = true;
    setHoveredCoordinate(null);
    setInvalidMoveCoordinate(null);
    setIsMokaThinking(true);
    setPosition(currentPosition => ({
      ...currentPosition,
      gameMessage: "Moka is thinking…",
      gameState: humanMoveResult.nextState,
      mokaResponseDurationMs: null,
      stones: humanMoveResult.stones
    }));
    void playMokaResponse(humanMoveResult, gameId, humanMovePolicyPercentilePromise);
  };
  const playCoordinate = (coordinate, didTriggerHapticDirectly = false) => playHumanMove(playMokaCoordinate(gameState, coordinate), coordinate, didTriggerHapticDirectly);
  const passTurn = (didTriggerHapticDirectly = false) => playHumanMove(passMokaTurn(gameState), null, didTriggerHapticDirectly);
  const playDebugMove = async () => {
    if (!isDebugMode || !isEngineReady || isGameComplete || isMokaThinking || errorMessage !== null || isMovePendingReference.current) {
      return;
    }
    const gameId = activeGameIdReference.current;
    const responseStartTime = performance.now();
    isMovePendingReference.current = true;
    setIsMokaThinking(true);
    setPosition(currentPosition => ({
      ...currentPosition,
      gameMessage: `Analyzing move ${gameState.moveCount + 1}…`,
      mokaResponseDurationMs: null
    }));
    try {
      const mokaMoveResult = await playMokaMove(gameState, Math.random());
      if (activeGameIdReference.current !== gameId) {
        return;
      }
      const responseDurationMs = performance.now() - responseStartTime;
      const positionProgress = {
        humanMovePolicyPercentileCount: position.humanMovePolicyPercentileCount,
        humanMovePolicyPercentileSum: position.humanMovePolicyPercentileSum,
        mokaResponseDurationMs: responseDurationMs
      };
      if (isMokaGameOver(mokaMoveResult.nextState)) {
        debugInferenceDurationMsReference.current = responseDurationMs;
        completeGame(mokaMoveResult.nextState, positionProgress);
        return;
      }
      const nextBlackWinProbability = await getMokaSymmetryBlackWinProbability(mokaMoveResult.nextState);
      if (activeGameIdReference.current !== gameId) {
        return;
      }
      debugInferenceDurationMsReference.current = performance.now() - responseStartTime;
      const playedMove = mokaMoveResult.nextState.moveHistory.at(-1);
      const playedColorLabel = playedMove?.color === "black" ? "Black" : "White";
      isMovePendingReference.current = false;
      setIsMokaThinking(false);
      commitPosition({
        ...positionProgress,
        blackWinProbability: nextBlackWinProbability,
        gameMessage: playedMove?.coordinate ? `${playedColorLabel} played ${getMokaCoordinateLabel(playedMove.coordinate)}.` : `${playedColorLabel} passed.`,
        gameState: mokaMoveResult.nextState,
        isGameComplete: false,
        stones: mokaMoveResult.stones
      });
    } catch (error) {
      if (activeGameIdReference.current !== gameId) {
        return;
      }
      isMovePendingReference.current = false;
      setErrorMessage(error instanceof Error ? error.message : "Moka could not analyze a move.");
      setIsDebugAutoPlaying(false);
      setIsMokaThinking(false);
    }
  };
  const restorePosition = nextHistoryIndex => {
    const historyPosition = positionHistoryReference.current[nextHistoryIndex];
    if (!historyPosition) {
      return;
    }
    activeGameIdReference.current += 1;
    isMovePendingReference.current = false;
    setErrorMessage(null);
    setHoveredCoordinate(null);
    setHistoryIndex(nextHistoryIndex);
    setInvalidMoveCoordinate(null);
    setIsMokaThinking(false);
    setPosition(historyPosition);
  };
  const resetGame = nextIsDebugMode => {
    const initialPosition = getMokaMatchInitialPosition(nextIsDebugMode ? "Debug ready." : "Your turn.");
    activeGameIdReference.current += 1;
    isMovePendingReference.current = false;
    positionHistoryReference.current = [initialPosition];
    setErrorMessage(null);
    setHoveredCoordinate(null);
    setHistoryIndex(0);
    setInvalidMoveCoordinate(null);
    setIsDebugAutoPlaying(nextIsDebugMode);
    setIsMokaThinking(false);
    setPosition(isEngineReady ? initialPosition : {
      ...initialPosition,
      gameMessage: "Loading Moka…"
    });
  };
  const startPlayableGame = () => {
    setIsDebugMode(false);
    resetGame(false);
  };
  const hoverCoordinate = coordinate => {
    if (isBoardDisabled) {
      setHoveredCoordinate(null);
      return;
    }
    setHoveredCoordinate(currentCoordinate => currentCoordinate?.column === coordinate.column && currentCoordinate.row === coordinate.row ? currentCoordinate : coordinate);
  };
  useEffect(() => {
    if (!isDebugAutoPlaying || !isDebugMode || !isEngineReady || isGameComplete || isMokaThinking || errorMessage !== null) {
      return;
    }
    const cooldownDurationMs = Math.max(MOKA_DEBUG_SELF_PLAY_MINIMUM_COOLDOWN_MS, debugInferenceDurationMsReference.current * MOKA_DEBUG_SELF_PLAY_COOLDOWN_MULTIPLIER);
    return scheduleBrowserIdleCallback(() => {
      void playDebugMove();
    }, cooldownDurationMs);
  }, [blackWinProbability, errorMessage, gameState, isDebugAutoPlaying, isDebugMode, isEngineReady, isGameComplete, isMokaThinking]);
  useEffect(() => {
    if (!isDebugMode || !isGameComplete) {
      return;
    }
    resetGame(true);
  }, [isDebugMode, isGameComplete]);
  return {
    areaAnalysis,
    blackMoveAnalyses,
    canRedo,
    canUndo,
    clearHoveredCoordinate: () => setHoveredCoordinate(null),
    clearInvalidMove: () => setInvalidMoveCoordinate(null),
    doesNeedDirectHapticTarget,
    errorMessage,
    estimatedRank,
    goToNextPosition: () => restorePosition(historyIndex + 1),
    goToPreviousPosition: () => restorePosition(historyIndex - 1),
    hoverCoordinate,
    hoveredCoordinate,
    invalidMoveCoordinate,
    isAnalysisLoading,
    isAnalysisModeActive,
    isAnalysisModePinned,
    isBoardDisabled,
    isDebugMode,
    isEngineReady,
    isHoveredMoveIllegal,
    isMokaThinking,
    latestMove,
    passTurn,
    playCoordinate,
    position,
    predictedBlackWinProbability: hoveredMoveAnalysis?.blackWinProbability ?? null,
    setIsAnalysisShortcutActive,
    startNewGame: () => resetGame(isDebugMode),
    startPlayableGame,
    toggleAnalysisMode: () => setIsAnalysisModePinned(isCurrentlyPinned => !isCurrentlyPinned),
    winner
  };
};
export { useMokaMatch };